// Pannello di configurazione integrazioni ERP (SAP, Zucchetti, TeamSystem)
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Input } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { CredentialsNote } from './CredentialsNote'
import api from '../../../lib/api'

type ERPName = 'sap' | 'zucchetti' | 'teamsystem'

const schema = z.object({
  syncSchedule: z.enum(['manual', 'hourly', 'daily', 'weekly']).default('daily'),
  importCustomers: z.boolean().default(true),
  exportOrders: z.boolean().default(true),
  syncInvoices: z.boolean().default(true),
  defaultCurrency: z.string().length(3).default('EUR'),
})

type FormValues = z.infer<typeof schema>

interface ERPConfigProps {
  integrationName: ERPName
  displayName: string
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

const credentialsByErp: Record<ERPName, string[]> = {
  sap: ['SAP_API_URL', 'SAP_USERNAME', 'SAP_PASSWORD', 'SAP_CLIENT'],
  zucchetti: ['ZUCCHETTI_API_URL', 'ZUCCHETTI_CLIENT_ID', 'ZUCCHETTI_CLIENT_SECRET'],
  teamsystem: ['TEAMSYSTEM_API_URL', 'TEAMSYSTEM_API_KEY', 'TEAMSYSTEM_COMPANY_ID'],
}

export function ERPConfig({ integrationName, displayName, currentMetadata, onSaved }: ERPConfigProps) {
  const qc = useQueryClient()

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      syncSchedule: (currentMetadata?.['syncSchedule'] as FormValues['syncSchedule']) ?? 'daily',
      importCustomers: (currentMetadata?.['importCustomers'] as boolean) ?? true,
      exportOrders: (currentMetadata?.['exportOrders'] as boolean) ?? true,
      syncInvoices: (currentMetadata?.['syncInvoices'] as boolean) ?? true,
      defaultCurrency: (currentMetadata?.['defaultCurrency'] as string) ?? 'EUR',
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put(`/integrations/${integrationName}/config`, { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  return (
    <div className="space-y-5">
      <CredentialsNote envVars={credentialsByErp[integrationName]} />

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Frequenza sincronizzazione automatica
          </label>
          <select
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('syncSchedule')}
          >
            <option value="manual">Solo manuale</option>
            <option value="hourly">Ogni ora</option>
            <option value="daily">Giornaliera (notte)</option>
            <option value="weekly">Settimanale</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Valuta di default
          </label>
          <Input
            type="text"
            maxLength={3}
            placeholder="EUR"
            {...register('defaultCurrency')}
            error={errors.defaultCurrency?.message}
          />
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-[var(--fc-foreground)] mb-2">
            Operazioni abilitate
          </legend>
          <div className="space-y-2">
            {[
              { key: 'importCustomers', label: 'Import anagrafica clienti' },
              { key: 'exportOrders', label: 'Export ordini verso ' + displayName },
              { key: 'syncInvoices', label: 'Sincronizzazione fatture' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={key}
                  className="h-4 w-4 rounded border-[var(--fc-border)]"
                  {...register(key as keyof FormValues)}
                />
                <label htmlFor={key} className="text-sm text-[var(--fc-foreground)]">
                  {label}
                </label>
              </div>
            ))}
          </div>
        </fieldset>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>
            Annulla
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending}>
            Salva configurazione
          </Button>
        </div>
      </form>
    </div>
  )
}
